/**
 * @fileoverview Privacy Policy Page
 *
 * Purpose: Public privacy policy for Morgan / Raconteur Operations Pty Ltd.
 *          Content is hosted directly in the app (self-contained) for a
 *          professional, branded presentation.
 * URL: /privacy
 * Protection: Public (no auth required)
 */

import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "./_home/-ui.legal-layout";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="Last updated: 17 September 2026"
    >
      <h2>1. Introduction</h2>
      <p>
        Raconteur Operations Pty Ltd (trading as Morgan) ABN 37 675 499 108 (
        <strong>we</strong>, <strong>us</strong> and <strong>our</strong>) is
        committed to protecting your privacy and handling your personal
        information responsibly. This Privacy Policy explains how we collect,
        use, disclose and hold personal information and how to contact us if you
        have any queries or wish to make a complaint about personal information
        that we hold about you.
      </p>

      <h2>2. Scope of this policy</h2>
      <p>
        Morgan provides a data ingestion, document processing and workflow
        software platform and related services (<strong>Services</strong>) to
        insolvency and restructuring practitioners (<strong>Subscribers</strong>
        ).
      </p>
      <p>
        This policy covers Morgan only, including personal information we
        receive from or on behalf of our Subscribers. It does not cover the
        information handling practices of our Subscribers or any other third
        parties from whom we receive personal information, who have their own
        privacy policies.
      </p>
      <p>
        The Services may contain links to third-party websites. This Privacy
        Policy does not apply to those sites. We encourage you to review the
        privacy policies of any third-party sites you visit.
      </p>

      <h2>3. What is personal information?</h2>
      <p>
        'Personal information' is information or an opinion about an identified
        individual, or an individual who is reasonably identifiable, whether the
        information or opinion is true or not and whether the information or
        opinion is recorded in a material form or not. Examples include an
        individual's name, address, contact number and email address.
      </p>
      <p>
        'Sensitive information' is a special category of personal information.
        Sensitive information includes health information and information about
        an individual's race or ethnic origin, religious or philosophical
        beliefs, membership of a professional or trade association, membership
        of a trade union, and genetic or biometric information or biometric
        templates.
      </p>

      <h2>4. Personal information we collect and hold</h2>
      <p>
        We collect and hold a range of personal information in carrying out our
        business and functions.
      </p>
      <p>
        The kinds of personal information we collect and hold about you will
        depend upon the nature of our relationship with you and the
        circumstances of a collection, including whether we collect the
        information from you as an existing or prospective Subscriber, user or
        account holder, or as a supplier, contractor, job applicant, or in some
        other capacity, or from a Subscriber or third party as part of our
        Services.
      </p>
      <p>The kinds of information we collect and hold include:</p>
      <ul>
        <li>
          <strong>Account information:</strong> When you or your employer
          creates a Morgan account, we collect your name, email address, job
          title, firm name, and account credentials.
        </li>
        <li>
          <strong>Onboarding and setup information:</strong> During account
          setup or onboarding, information you provide to us about your firm and
          workflow preferences.
        </li>
        <li>
          <strong>Communications:</strong> When you contact us for support, to
          provide feedback, or for any other purpose, we collect the information
          you provide, including your name, email address, the content of your
          message, and any other details of the interactions you have with us.
        </li>
        <li>
          <strong>Log data:</strong> When you access the Services, our servers
          automatically record your IP address, browser type and version, the
          pages or features you access, and the date and time of each request.
        </li>
        <li>
          <strong>Usage data:</strong> When you use the Services, we collect
          information about how you use the Services, including features
          accessed, actions taken, session duration and query volume.
        </li>
        <li>
          <strong>Device information:</strong> When you access the Services, we
          collect information about the device you use to access the Services,
          including operating system, browser type and device identifiers.
        </li>
        <li>
          <strong>Client documents:</strong> Morgan is a data ingestion,
          document processing and workflow software platform. We may receive
          information about you in client documents, case data, or other content
          uploaded to or processed through our platform and related services.
        </li>
        <li>
          <strong>Job applicants:</strong> If you apply for a job with us, we
          collect your name and contact details, date of birth, identification
          documents, qualifications and employment history, referee contact
          details, police and background checks, and other information required
          to assess and manage an application.
        </li>
        <li>
          <strong>Others:</strong> Other personal information that:
          <ul>
            <li>
              you provide to us directly through the Services or our website;
              and
            </li>
            <li>
              we require from time to time to manage your business relationship
              with us.
            </li>
          </ul>
        </li>
      </ul>

      <h3>4.1 Sensitive information</h3>
      <p>
        We only collect, use and disclose sensitive information about you with
        your consent, except where required or permitted by or under law. We ask
        that you only share sensitive information with us if it is relevant to
        the Services we are providing or our interaction with you.
      </p>
      <p>
        To the extent we collect sensitive information, we generally collect
        sensitive information directly from you. However, we may also collect it
        from third parties (such as our Subscribers or third party platforms to
        which our Subscribers grant us access in connection with the Services)
        with your consent, unless otherwise required or permitted by or under
        law.
      </p>
      <p>
        If you provide us with personal information about another person, please
        make sure that you tell them about this privacy policy.
      </p>

      <h2>5. How we collect personal information</h2>
      <p>We collect personal information in a variety of ways, including:</p>
      <ul>
        <li>
          from you directly (such as when you interact with us in writing,
          electronically or by telephone);
        </li>
        <li>when you communicate with us through our website;</li>
        <li>
          from third parties such as our Subscribers, and third-party platforms
          to which our Subscribers grant us access (including via API) to enable
          us to provide the Services;
        </li>
        <li>law enforcement agencies and other government entities;</li>
        <li>third-party identity verification service providers; and</li>
        <li>
          publicly available sources of information (e.g. public registers or
          social media).
        </li>
      </ul>

      <h2>6. Why we collect, hold, use and disclose personal information</h2>
      <p>
        We collect, hold, use and disclose personal information for a range of
        purposes including:
      </p>
      <ul>
        <li>to provide our products and Services;</li>
        <li>to manage your account and access to our products and Services;</li>
        <li>to respond to your enquiries about our products and Services;</li>
        <li>
          to perform research and analysis and improve or develop our products
          and Services;
        </li>
        <li>
          to maintain the security of, detect fraud in relation to, and prevent
          misuse of our products and Services;
        </li>
        <li>to process payments made to us;</li>
        <li>for our administrative purposes and internal record keeping;</li>
        <li>
          to provide you with customer service or technical support and deal
          with any complaints or feedback you have;
        </li>
        <li>
          to manage our relationships with our clients, Subscribers, suppliers
          and contractors;
        </li>
        <li>
          to consider job applicants for current and future employment; and
        </li>
        <li>to comply with our legal or regulatory obligations.</li>
      </ul>
      <p>
        We may use and disclose your personal information for other purposes
        required or authorised by or under law (including purposes for which you
        have provided your consent).
      </p>

      <h2>7. Direct marketing</h2>
      <p>
        We may use your personal information so we can contact you with
        information about our products and services, special offers, promotions
        and events that may be of interest to you.
      </p>
      <p>
        We may contact you by email, mail or telephone. You can let us know at
        any time if you no longer wish to receive these communications, by
        contacting us (using the contact details at the end of this policy) or
        using the opt-out/unsubscribe facility in our communications.
      </p>

      <h2>8. Disclosure of personal information to third parties</h2>
      <p>
        In conducting our business, we may disclose your personal information to
        third parties for the purposes outlined above. These third parties may
        include, where appropriate:
      </p>
      <ul>
        <li>our related entities;</li>
        <li>
          our employees, contractors and advisers who need access to provide the
          Services;
        </li>
        <li>
          our Subscribers and their employees, contractors and advisors in
          connection with the Services;
        </li>
        <li>financial institutions for payment processing;</li>
        <li>external business advisers (such as auditors and lawyers);</li>
        <li>
          where required or authorised by law, including to regulators, courts
          or law enforcement agencies; and
        </li>
        <li>
          if you apply for a job with us, referees whose details you provide to
          us.
        </li>
      </ul>
      <p>
        We may also disclose your personal information to other third parties
        and for other purposes where we are required or authorised by or under
        law to do so (including where you have provided your consent).
      </p>
      <p>
        We may expand, reduce or sell our business (in whole or in part) from
        time to time. Personal information we hold, where it is relevant to any
        part of the business for sale and/or transfer, may be disclosed to a
        proposed new owner or new controlling entity for their due diligence
        purposes and upon completion of a sale or transfer to be used for the
        purposes described in this Privacy Policy.
      </p>

      <h2>9. Overseas disclosures of personal information</h2>
      <p>
        Some of our service providers and related entities are located outside
        Australia. As a result, personal information collected and held by us
        may be transferred to recipients in other countries, including the
        locations specified on our website at:{" "}
        <a
          href="https://trust.morgan.so/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://trust.morgan.so/
        </a>
        .
      </p>

      <h2>10. How we hold personal information</h2>
      <p>
        We generally hold personal information on infrastructure provided by
        Convex, with the Services hosted on Vercel. We implement reasonable
        technical and organisational measures to protect personal information
        from misuse, interference, loss, and unauthorised access, modification
        or disclosure. This includes taking appropriate security measures to
        protect electronic materials (such as access controls and encryption in
        transit) and conducting regular security reviews.
      </p>
      <p>
        Our third party data storage providers are required to protect personal
        information in accordance with applicable laws and take appropriate
        technical and organisational measures against unauthorised or unlawful
        use of personal information or its accidental loss, destruction or
        damage.
      </p>
      <p>
        No method of transmission or storage is completely secure. If you become
        aware of any security incident affecting your account or personal
        information, please notify us immediately at{" "}
        <a href="mailto:legal@morgan.so">legal@morgan.so</a>.
      </p>

      <h2>11. Cookies</h2>
      <p>
        We use cookies and similar tracking technologies to support platform
        functionality, remember your preferences, and understand how the
        Services are used.
      </p>
      <p>
        We use the following types of cookies on our websites and platforms:
      </p>
      <ul>
        <li>
          <strong>Essential cookies:</strong> These cookies are required for the
          platform to function, including authentication and session management.
        </li>
        <li>
          <strong>Analytics cookies:</strong> We use analytics cookies via
          PostHog to understand how users interact with our services and
          platform. Usage data is anonymised and aggregated, and no document
          content is included in analytics data.
        </li>
        <li>
          <strong>Functional cookies:</strong> These cookies are used to
          remember your preferences and settings.
        </li>
      </ul>
      <p>
        You can manage or disable cookies through your browser settings, though
        doing so may affect the functionality of the services.
      </p>

      <h2>12. Access to and correction of your personal information</h2>
      <p>
        You have a right to request access to personal information that we hold
        about you and request its correction if it is inaccurate, out of date,
        incomplete, irrelevant or misleading. You may do so by contacting us
        using the details below.
      </p>
      <p>
        We will respond to all requests for access to or correction of personal
        information within a reasonable period.
      </p>
      <p>
        We will generally provide you with access to your personal information
        (subject to some exceptions permitted by law) but may charge an access
        fee to cover the cost of retrieving the information and supplying it to
        you.
      </p>

      <h2>13. Complaints</h2>
      <p>
        Please contact us (using the contact details below) if you have any
        concerns or complaints about the manner in which we have collected or
        handled your personal information. We will review your complaint and
        respond within a reasonable period of time (usually 30 days). We may
        contact you during the process to seek further clarification if
        necessary.
      </p>
      <p>
        If you are not satisfied with our response, you can contact us to
        discuss your concerns or lodge a complaint with the Office of the
        Australian Information Commissioner (
        <a
          href="https://www.oaic.gov.au"
          target="_blank"
          rel="noopener noreferrer"
        >
          oaic.gov.au
        </a>
        ).
      </p>

      <h2>14. Contact details</h2>
      <p>
        If you would like more information about the way we manage personal
        information, would like to request access to or correction of personal
        information that we hold about you, or wish to make a complaint, please
        contact us by either:
      </p>
      <p>
        Post: Raconteur Operations Pty Ltd (trading as Morgan)
        <br />
        40 Proctors Road, Dynnyrne 7005 Tasmania
        <br />
        Email: <a href="mailto:legal@morgan.so">legal@morgan.so</a>
      </p>

      <h2>15. Changes to our privacy policy</h2>
      <p>
        We may update our Privacy Policy from time to time. For the current
        version of our Privacy Policy, please visit our website at www.morgan.so
        or contact us at the above details.
      </p>
    </LegalLayout>
  );
}
